import React, { useEffect, useState } from 'react'
import { Alert, Box } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import { post } from '../http/restservice'
import LoadingButton from '@mui/lab/LoadingButton'
import ReactGA4 from 'react-ga4'
import PostHogTracker from '../utils/PostHogTracker'
import { isValidPhoneNumber as matchIsValidPhoneNumber } from 'libphonenumber-js'
import { AuthFloatingPhoneInput } from '../Components/Auth/AuthFloatingInput'

const LoginPage = () => {
  const navigate = useNavigate()
  const [phoneNumber, setPhoneNumber] = useState('')
  const isValidPhoneNumber = matchIsValidPhoneNumber(phoneNumber ?? '')

  const [message, setMessage] = useState('')
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [partnerUser, setPartnerUser] = useState(false)
  const [severity, setSeverity] = useState('error')

  // const handleChange = (e) => {
  //   setPhoneNumber(e.target.value?.replace(/[^0-9]/g, ''))
  // }

  const handlePhoneNumberChange = (value) => {
    setPhoneNumber(value ?? '')
    setPartnerUser(false)
    if (message) {
      setMessage('')
      setSeverity('error')
    }
  }

  const getResponseMessage = (response) => {
    if (typeof response?.response === 'string') return response.response
    return (
      response?.response?.message ||
      response?.message ||
      'Unable to process your request. Please try again.'
    )
  }

  useEffect(() => {
    const userMobile = localStorage.getItem('access_token')

    if (userMobile) {
      if (window.utmData && window.utmData['$marketing_title']) {
        if (window.utmData['$marketing_title'] === 'Insurance') {
          navigate('/dashboard/insurance')
        } else {
          let navState = null
          if (window.utmData.navigation_state) {
            navState = JSON.parse(window.utmData.navigation_state)
            // console.log(navState)
          }
          if (window.utmData.site_url) {
            navigate(window.utmData.site_url, { state: navState })
          } else {
            navigate('/dashboard')
          }
        }
      } else {
        navigate('/dashboard')
      }
    } else {
      ReactGA4.event({
        category: 'Login',
        action: 'Login Viewed',
      })
      PostHogTracker.pageViewed('Login Page')
    }
    if (location) {
      setMessage(location.state?.message)
      setSeverity(location.state?.severity)
    }
  }, [])

  // useEffect(() => {
  //   PostHogTracker.pageViewed('Login Page')
  //   document.getElementById('component-outlined').blur()
  // }, [])

  const validateLogin = async (event) => {
    event?.preventDefault()
    if (partnerUser || !isValidPhoneNumber || loading) return

    setMessage('')
    setSeverity('error')
    setLoading(true)
    ReactGA4.event({
      category: 'Login',
      action: 'Login phone Entered',
    })

    const payload = {
      phoneNumber,
    }

    PostHogTracker.phoneNumberEntered('Login')

    try {
      const response = await post('/customerservice/v2/api/customer/validate', payload)
      setLoading(false)
      if (response?.isError) {
        setMessage(getResponseMessage(response))
        setSeverity('error')
        return
      }

      if (!response) {
        navigate('/sign-up', {
          state: {
            phoneNumber,
          },
        })
        return
      }

      if (response.isPartnerUser) {
        setMessage(response.message)
        setPartnerUser(true)
        // window.open(response.redirectUrl, '_blank')
        return
      }

      if (response.isUserActive !== undefined && !response.isUserActive) {
        navigate('/activate-account-otp', {
          state: {
            phoneNumber,
            message: 'Account is not activated. Please activate your account and try logging in.',
          },
        })
        return
      }

      if (response.isUserActive) {
        navigate('/otp', {
          state: {
            phoneNumber,
          },
        })
        return
      }

      navigate('/sign-up', {
        state: {
          phoneNumber,
        },
      })
    } catch (e) {
      setLoading(false)
      setMessage('Unable to process your request. Please try again.')
      setSeverity('error')
      return
    }
  }

  return (
    <div className='mx-auto flex w-full max-w-[360px] min-w-0 flex-col items-center justify-center lg:max-w-[432px]'>
      <div className='mb-6 hidden w-full flex-col gap-y-1 lg:flex'>
        <h1 className='text-center font-IBM text-[28px] font-bold leading-9 text-black-950 lg:text-[36px] lg:leading-10 dark:text-[var(--fb-text)]'>
          Welcome
        </h1>
        <p className='text-center font-IBM text-sm font-medium leading-5 text-black-400 dark:text-black-300'>
          Sign in to continue
        </p>
      </div>
      <div className='w-full'>
        <Grid item xs={12}>
          {!partnerUser && message && <Alert severity={severity}>{message}</Alert>}
          {partnerUser && message && (
            <Alert severity={severity}>
              This account already exists on our partner platform. Please continue on{' '}
              <a href='https://wealthelite.in/'> RedVision’s wealth lite platform. </a>
            </Alert>
          )}
        </Grid>
        <Grid item>
          <form onSubmit={validateLogin} className='flex h-min min-w-0 flex-col font-IBM'>
            <AuthFloatingPhoneInput
              label='Enter Phone Number'
              phoneNumber={phoneNumber}
              setPhoneNumber={handlePhoneNumberChange}
              darkSurface
              required
            />

            <LoadingButton
              variant='contained'
              loading={loading}
              sx={{
                mt: '24px',
                height: '40px',
                px: '12px',
                fontSize: '14px',
                lineHeight: '20px',
                fontWeight: '600',
                fontFamily: 'IBM Plex Sans',
                textTransform: 'none',
                borderRadius: '12px',
                boxShadow: '0 10px 24px rgba(40, 24, 255, 0.18)',
                '&:hover': {
                  backgroundColor: '#2818ff',
                  boxShadow: '0 10px 24px rgba(40, 24, 255, 0.18)',
                },
                '&:active': { backgroundColor: '#2818ff', boxShadow: 'none' },
                '&.Mui-disabled': {
                  backgroundColor: '#97aaff',
                  color: '#fff',
                  boxShadow: 'none',
                  opacity: 1,
                },
              }}
              className='login-btn'
              fullWidth
              type='button'
              disabled={partnerUser || !isValidPhoneNumber || loading}
              onClick={validateLogin}
            >
              Continue
            </LoadingButton>
          </form>
        </Grid>
        <Grid item xs={12} sx={{ textAlign: 'center' }} className='!hidden lg:!block'>
          <Box className='mt-6 flex items-center justify-center gap-x-1.5'>
            <p className='font-IBM text-sm font-medium leading-5 text-black-400'>
              Don&apos;t have an account?
            </p>
            <button
              type='button'
              className='inline-flex items-center leading-5'
              onClick={() => {
                PostHogTracker.buttonClicked('Sign Up Link', {
                  action: 'Sign Up',
                  page_name: 'Login',
                })
                navigate('/sign-up', {
                  state: {
                    phoneNumber,
                  },
                })
              }}
            >
              <span className='border-b-2 border-dashed border-primary-500 font-IBM text-sm font-semibold leading-5 text-primary-500'>
                Register
              </span>
            </button>
          </Box>
        </Grid>
      </div>
    </div>
  )
}

export default LoginPage
